import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getListAPIKeysQueryKey,
  useListAPIKeys,
  useRevokeAPIKey,
  type APIKeyCreatedDTO,
  type APIKeyDTO,
} from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { FormSection } from '@/components/FormSection';
import { CreateApiKeyDialog } from '@/components/User/CreateApiKeyDialog';
import { CreatedApiKeyDialog } from '@/components/User/CreatedApiKeyDialog';
import { WarningDialog } from '@/components/WarningDialog';
import Button from '@/components/core/Button';
import { ErrorBanner } from '@/components/core/ErrorBanner';
import { Spinner } from '@/components/core/Spinner';
import { WarningBanner } from '@/components/core/WarningBanner';
import { useApiKeysDisabled } from '@/contexts/ConfigContext';
import { cn } from '@/helpers/classnames';
import { getApiErrorMessage } from '@/helpers/apiError';
import {
  getApiKeyStatus,
  sortApiKeys,
  type ApiKeyStatus,
} from '@/helpers/apiKey';
import { formatDateTime } from '@/helpers/formatting';
import { toast } from '@/hooks/useToast';
import {
  IconCheckCircle,
  IconExclamationTriangle,
  IconPlus,
  IconXMarkCircle,
} from '@/icons';
import { useAuth } from '@/stores/auth';

export const ApiKeyManagement = () => {
  const { formatMessage } = useIntl();
  const { userProfile } = useAuth();
  const queryClient = useQueryClient();
  const apiKeysDisabled = useApiKeysDisabled();
  const canManageApiKeys =
    !apiKeysDisabled &&
    checkUserPermissions({
      permission: 'organization:editor',
    });
  const { data, isLoading, isError, isFetching, refetch } = useListAPIKeys({
    query: { enabled: canManageApiKeys },
  });
  const { mutateAsync: revokeApiKey, isPending: isRevoking } =
    useRevokeAPIKey();
  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<APIKeyCreatedDTO | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<APIKeyDTO | null>(null);

  const isImpersonating = !!userProfile?.expires_at;
  const apiKeys = useMemo(() => sortApiKeys(data ?? []), [data]);

  const handleOpenCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const handleCreated = useCallback((apiKey: APIKeyCreatedDTO) => {
    setCreateOpen(false);
    setCreatedKey(apiKey);
  }, []);

  const handleCloseCreated = useCallback(() => {
    setCreatedKey(null);
  }, []);

  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleRevoke = useCallback((apiKey: APIKeyDTO) => {
    setRevokeTarget(apiKey);
  }, []);

  const handleCancelRevoke = useCallback(() => {
    if (isRevoking) {
      return;
    }
    setRevokeTarget(null);
  }, [isRevoking]);

  const handleConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) {
      return;
    }

    try {
      await revokeApiKey({ apiKeyID: revokeTarget.id });
      await queryClient.invalidateQueries({
        queryKey: getListAPIKeysQueryKey(),
      });
      toast.success(
        {
          title: formatMessage(
            { defaultMessage: 'API-nyckeln {name} återkallades' },
            { name: revokeTarget.name },
          ),
        },
        { autoClose: 3000 },
      );
      setRevokeTarget(null);
    } catch (error) {
      toast.error({
        title: formatMessage({
          defaultMessage: 'API-nyckeln kunde inte återkallas',
        }),
        description: getApiErrorMessage(
          error,
          formatMessage({
            defaultMessage: 'Något gick fel',
          }),
        ),
      });
    }
  }, [formatMessage, queryClient, revokeApiKey, revokeTarget]);

  if (!canManageApiKeys) {
    return null;
  }

  return (
    <FormSection
      id="api-keys"
      title={<FormattedMessage defaultMessage="API-nycklar" />}
      description={
        <FormattedMessage defaultMessage="En API-nyckel är till för att genom kod/API åstadkomma saker i portalen via ett externt system. När en nyckel skapas binds den till dig, vilket innebär att när det externa systemet gör saker så ser det ut som att du själv gjort dem. Det kan därför vara bra att skapa en påhittad administratör, vars enda syfte är att hantera API-nycklar för organisationen." />
      }
    >
      {isImpersonating ? (
        <WarningBanner>
          <FormattedMessage defaultMessage="En tillfällig profil kan inte skapa API-nycklar." />
        </WarningBanner>
      ) : (
        <Button
          className="w-full"
          startIcon={<IconPlus aria-hidden="true" />}
          onClick={handleOpenCreate}
        >
          <FormattedMessage defaultMessage="Skapa API-nyckel" />
        </Button>
      )}

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-8" />
        </div>
      ) : null}

      {isError ? (
        <ErrorBanner>
          <span className="flex flex-col items-start gap-2">
            <FormattedMessage defaultMessage="API-nycklarna kunde inte hämtas." />
            <Button variant="ghost" onClick={handleRetry} loading={isFetching}>
              <FormattedMessage defaultMessage="Försök igen" />
            </Button>
          </span>
        </ErrorBanner>
      ) : null}

      {!isLoading && !isError && apiKeys.length === 0 ? (
        <i className="text-text-700">
          <FormattedMessage defaultMessage="Inga API-nycklar skapade" />
        </i>
      ) : null}

      {!isLoading && !isError && apiKeys.length > 0 ? (
        <ul className="divide-y divide-background-300">
          {apiKeys.map((apiKey) => (
            <ApiKeyListItem
              key={apiKey.id}
              apiKey={apiKey}
              onRevoke={handleRevoke}
            />
          ))}
        </ul>
      ) : null}

      {createOpen ? (
        <CreateApiKeyDialog
          onClose={handleCloseCreate}
          onCreated={handleCreated}
        />
      ) : null}
      {createdKey ? (
        <CreatedApiKeyDialog apiKey={createdKey} onClose={handleCloseCreated} />
      ) : null}
      {revokeTarget ? (
        <WarningDialog
          open
          title={formatMessage({ defaultMessage: 'Återkalla API-nyckel' })}
          content={
            <p>
              <FormattedMessage
                defaultMessage="{name} ({prefix}...) slutar fungera direkt. Det går inte att ångra."
                values={{
                  name: revokeTarget.name,
                  prefix: (
                    <span className="font-mono">{revokeTarget.key_prefix}</span>
                  ),
                }}
              />
            </p>
          }
          buttonText={formatMessage({ defaultMessage: 'Återkalla' })}
          onConfirm={() => {
            void handleConfirmRevoke();
          }}
          onCancel={handleCancelRevoke}
          loading={isRevoking}
        />
      ) : null}
    </FormSection>
  );
};

interface ApiKeyListItemProps {
  apiKey: APIKeyDTO;
  onRevoke: (apiKey: APIKeyDTO) => void;
}

const ApiKeyListItem = ({ apiKey, onRevoke }: ApiKeyListItemProps) => {
  const { formatMessage } = useIntl();
  const status = getApiKeyStatus(apiKey);

  const handleRevoke = useCallback(() => {
    onRevoke(apiKey);
  }, [apiKey, onRevoke]);

  return (
    <li className="flex flex-col gap-1 py-4">
      <p className="min-w-0 break-words font-medium">{apiKey.name}</p>
      <div className="flex flex-col gap-1">
        <ApiKeyDetail mono label={<FormattedMessage defaultMessage="Nyckel" />}>
          {apiKey.key_prefix}...
        </ApiKeyDetail>
        <ApiKeyDetail
          label={<FormattedMessage defaultMessage="Giltighetstid" />}
        >
          <div className="flex items-center gap-2">
            <span>{formatDateTime(apiKey.created_at)}</span> -{' '}
            {apiKey.expires_at ? (
              <span>{formatDateTime(apiKey.expires_at)}</span>
            ) : (
              <i className="text-text-700">
                <FormattedMessage defaultMessage="Går inte ut" />
              </i>
            )}
            <div className="ml-6">
              <ApiKeyStatusBadge status={status} />
            </div>
          </div>
        </ApiKeyDetail>
        <ApiKeyDetail
          label={<FormattedMessage defaultMessage="Senast använd" />}
        >
          {apiKey.last_used_at ? (
            formatDateTime(apiKey.last_used_at)
          ) : (
            <i className="text-text-700">
              <FormattedMessage defaultMessage="Inte använd" />
            </i>
          )}
        </ApiKeyDetail>
        {apiKey.revoked_at ? (
          <ApiKeyDetail
            label={<FormattedMessage defaultMessage="Återkallad" />}
          >
            {formatDateTime(apiKey.revoked_at)}
          </ApiKeyDetail>
        ) : null}
      </div>
      {status !== 'revoked' ? (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="outline"
            color="red"
            aria-label={formatMessage(
              { defaultMessage: 'Återkalla {name}' },
              { name: apiKey.name },
            )}
            onClick={handleRevoke}
          >
            <FormattedMessage defaultMessage="Återkalla" />
          </Button>
        </div>
      ) : null}
    </li>
  );
};

interface ApiKeyDetailProps {
  label: ReactNode;
  children: ReactNode;
  mono?: boolean;
}

const ApiKeyDetail = ({ label, children, mono = false }: ApiKeyDetailProps) => (
  <div className="flex flex-col sm:flex-row sm:gap-2">
    <span className="text-text-700 sm:w-36 sm:shrink-0">{label}</span>
    <span className={cn('min-w-0 break-words', mono && 'font-mono')}>
      {children}
    </span>
  </div>
);

const ApiKeyStatusBadge = ({ status }: { status: ApiKeyStatus }) => {
  if (status === 'revoked') {
    return (
      <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-background-400 px-2 text-sm text-text-800">
        <IconXMarkCircle className="size-4" aria-hidden="true" />
        <FormattedMessage defaultMessage="Återkallad" />
      </span>
    );
  }

  if (status === 'expired') {
    return (
      <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-yellow-500 px-2 text-sm text-contrast-yellow">
        <IconExclamationTriangle className="size-4" aria-hidden="true" />
        <FormattedMessage defaultMessage="Utgången" />
      </span>
    );
  }

  return (
    <span className="flex w-fit shrink-0 items-center gap-1 rounded-full bg-green-500 px-2 text-sm text-contrast-green">
      <IconCheckCircle className="size-4" aria-hidden="true" />
      <FormattedMessage defaultMessage="Aktiv" />
    </span>
  );
};
